import { VC_AGENT_SESSION_SUPPLIER_DISCOVERY_ITEMS_TAG } from 'store/tags/valueChainSession';

import type IAgentSessionSupplierDiscoveryItem
  from 'store/slices/valueChainMap/interfaces/IAgentSessionSupplierDiscoveryItem';
import type IGetAgentSessionSupplierDiscoveryItemsRequest
  from 'store/slices/valueChainMap/interfaces/IGetAgentSessionSupplierDiscoveryItemsRequest';
import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type { TApi } from 'store/types/TApi';
import type { AppDispatch, RootState } from 'store/types/TStore';

type TSupplierDiscoveryItemsApi = TApi<
IGetAgentSessionSupplierDiscoveryItemsRequest,
IAgentSessionSupplierDiscoveryItem[],
'getAgentSessionSupplierDiscoveryItems'
>;

type TSupplierDiscoveryItemsCache = IBaseQueryPaginationResponse<IAgentSessionSupplierDiscoveryItem[]>;

const recalculateMeta = (cache: TSupplierDiscoveryItemsCache): TSupplierDiscoveryItemsCache => {
  const { meta, items } = cache.data;

  return {
    ...cache,
    data: {
      ...cache.data,
      meta: {
        ...meta,
        hasNext: meta.count > meta.offset + items.length,
      },
    },
  };
};

const updateSupplierDiscoveryItemsQueries = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  unitId,
  updater,
}: {
  valueChainMapApi: TSupplierDiscoveryItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  unitId: number,
  updater: (
    cache: TSupplierDiscoveryItemsCache,
    originalArgs: IGetAgentSessionSupplierDiscoveryItemsRequest,
  ) => TSupplierDiscoveryItemsCache,
}) => {
  valueChainMapApi.util.selectInvalidatedBy(state, [{
    type: VC_AGENT_SESSION_SUPPLIER_DISCOVERY_ITEMS_TAG,
    id: `${valueChainMapId}-${sessionId}-${unitId}`,
  }]).forEach(({ endpointName, originalArgs }) => {
    if (endpointName !== 'getAgentSessionSupplierDiscoveryItems') {
      return;
    }

    dispatch(valueChainMapApi.util.updateQueryData(
      endpointName,
      originalArgs as IGetAgentSessionSupplierDiscoveryItemsRequest,
      (cache) => {
        const updatedCache = updater(
          cache as TSupplierDiscoveryItemsCache,
          originalArgs as IGetAgentSessionSupplierDiscoveryItemsRequest,
        );

        return recalculateMeta(updatedCache);
      },
    ));
  });
};

export const syncCreatedSupplierDiscoveryItemInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  unitId,
  item,
}: {
  valueChainMapApi: TSupplierDiscoveryItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  unitId: number,
  item: IAgentSessionSupplierDiscoveryItem,
}) => {
  updateSupplierDiscoveryItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    unitId,
    updater: (cache, originalArgs) => {
      const nextMeta = {
        ...cache.data.meta,
        count: cache.data.meta.count + 1,
      };

      if ((originalArgs.offset || 0) !== 0) {
        return {
          ...cache,
          data: {
            ...cache.data,
            meta: nextMeta,
          },
        };
      }

      const nextItems = [item, ...cache.data.items.filter((cachedItem) => cachedItem.id !== item.id)];
      const limit = originalArgs.limit ?? cache.data.meta.limit;

      return {
        ...cache,
        data: {
          ...cache.data,
          items: nextItems.slice(0, limit),
          meta: nextMeta,
        },
      };
    },
  });
};

export const syncUpdatedSupplierDiscoveryItemInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  unitId,
  item,
}: {
  valueChainMapApi: TSupplierDiscoveryItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  unitId: number,
  item: IAgentSessionSupplierDiscoveryItem,
}) => {
  updateSupplierDiscoveryItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    unitId,
    updater: (cache) => {
      const itemIndex = cache.data.items.findIndex((cachedItem) => cachedItem.id === item.id);

      if (itemIndex === -1) {
        return cache;
      }

      return {
        ...cache,
        data: {
          ...cache.data,
          items: cache.data.items.map((cachedItem) => (
            cachedItem.id === item.id ? item : cachedItem
          )),
        },
      };
    },
  });
};

export const syncDeletedSupplierDiscoveryItemInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  unitId,
  itemId,
}: {
  valueChainMapApi: TSupplierDiscoveryItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  unitId: number,
  itemId: number,
}) => {
  updateSupplierDiscoveryItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    unitId,
    updater: (cache) => {
      const nextItems = cache.data.items.filter((cachedItem) => cachedItem.id !== itemId);

      if (nextItems.length === cache.data.items.length) {
        return cache;
      }

      return {
        ...cache,
        data: {
          ...cache.data,
          items: nextItems,
          meta: {
            ...cache.data.meta,
            count: Math.max(cache.data.meta.count - 1, 0),
          },
        },
      };
    },
  });
};

export const syncSupplierDiscoverySelectionInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  unitId,
  isActive,
}: {
  valueChainMapApi: TSupplierDiscoveryItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  unitId: number,
  isActive: boolean,
}) => {
  updateSupplierDiscoveryItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    unitId,
    updater: (cache) => ({
      ...cache,
      data: {
        ...cache.data,
        items: cache.data.items.map((item) => ({ ...item, isActive })),
      },
    }),
  });
};
